import { useMemo, useState } from "react";
import type { ArtistIntake, ConsentAcceptance } from "../../domain/schemas";
import { createMockIntake, runDarkRoomLiteMock } from "../../domain/darkRoom";

const demoConsent: ConsentAcceptance = {
  analysisConsent: true,
  rightsAttestation: true,
  aiDisclosureAcknowledged: true,
  marketingOptIn: false,
  benchmarkOptIn: false,
  version: "shadow-consent-v1"
};

const WHATIF_PRESETS: Array<{ label: string; description: string; goals: number; density: "thin" | "medium" | "dense"; sync: boolean }> = [
  { label: "Sync Artist", description: "Optimize for sync licensing placement", goals: 5, density: "dense", sync: true },
  { label: "Label Pitch", description: "Maximize for label A&R review", goals: 4, density: "dense", sync: false },
  { label: "Independent", description: "Direct-to-audience release path", goals: 3, density: "medium", sync: false }
];

export function WhatIfPanel({ currentScores, artistIntake }: { currentScores: Record<string, number>; artistIntake: ArtistIntake }) {
  const [whatIfGoals, setWhatIfGoals] = useState(artistIntake.goals.length);
  const [whatIfDensity, setWhatIfDensity] = useState<"thin" | "medium" | "dense">("medium");
  const [whatIfSync, setWhatIfSync] = useState(false);
  const [open, setOpen] = useState(false);

  const whatIfScores = useMemo(() => {
    const synthGoals = Array.from({ length: whatIfGoals }, (_, i) =>
      whatIfSync && i === 0 ? "sync licensing" : `goal-${i}`
    );
    const synthSongText = whatIfDensity === "thin" ? "x".repeat(100) : whatIfDensity === "medium" ? "x".repeat(250) : "x".repeat(400);
    const synthIntake: ArtistIntake = { ...artistIntake, goals: synthGoals };
    try {
      const artist = createMockIntake(synthIntake, demoConsent);
      return runDarkRoomLiteMock(artist, synthSongText).compositeScores;
    } catch {
      return null;
    }
  }, [artistIntake, whatIfGoals, whatIfDensity, whatIfSync]);

  return (
    <section className="panel whatIfPanel">
      <button type="button" className="whatIfToggle" onClick={() => setOpen((o) => !o)}>
        {open ? "Close What-if" : "What-if — explore how changes affect your score"}
      </button>
      {open && whatIfScores && (
        <div className="whatIfContent">
          <div className="whatIfPresets">
            {WHATIF_PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className="whatIfPresetBtn"
                title={preset.description}
                onClick={() => { setWhatIfGoals(preset.goals); setWhatIfDensity(preset.density); setWhatIfSync(preset.sync); }}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <p className="whatIfDescription">Adjust the sliders to see how different inputs change your Dark Room scores.</p>
          <div className="whatIfControls">
            <label className="whatIfControl">
              <span>Goals: {whatIfGoals}</span>
              <input type="range" min={0} max={8} value={whatIfGoals} onChange={(e) => setWhatIfGoals(Number(e.target.value))} />
            </label>
            <label className="whatIfControl">
              <span>Evidence density</span>
              <select value={whatIfDensity} onChange={(e) => setWhatIfDensity(e.target.value as "thin" | "medium" | "dense")}>
                <option value="thin">Thin</option>
                <option value="medium">Medium</option>
                <option value="dense">Dense</option>
              </select>
            </label>
            <label className="whatIfControl whatIfCheckbox">
              <input type="checkbox" checked={whatIfSync} onChange={(e) => setWhatIfSync(e.target.checked)} />
              <span>Include sync licensing goal</span>
            </label>
          </div>
          <div className="whatIfDeltas">
            {Object.entries(whatIfScores).map(([key, score]) => {
              const delta = score - (currentScores[key] ?? score);
              return (
                <div key={key} className="whatIfDelta">
                  <span className="whatIfDeltaLabel">{key}</span>
                  <span className={`whatIfDeltaChip ${delta > 0 ? "deltaPositive" : delta < 0 ? "deltaNegative" : "deltaNeutral"}`}>
                    {delta > 0 ? "+" : ""}{delta !== 0 ? Math.round(delta) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
